import { PolicyUi } from '@/common/Policy';
import { AmplifyModules } from '@/plugins/Amplify';
import store from '@/store/index';
import axios from 'axios';
import Policy from './Policy';
import Auth from './Auth';

const MINUTE = 60000;

const idleTime = 0;
const idleTimeout = 20;

const User = {
	trackUserInactivity() {
		const idleInterval = setInterval(async () => {
			if (getIdleTime() <= new Date().getTime() === true) {
				console.log('log out the user');
				await AmplifyModules.Auth.signOut();
				deleteIdleTime();
				store.dispatch('Auth/signout');
				clearInterval(idleInterval);
			}
		}, MINUTE / 2);

		const setIdleTime = (): void => {
			window.localStorage.setItem(
				'last-action',
				(new Date().getTime() + idleTimeout * MINUTE).toString(),
			);
		};

		const getIdleTime = (): number => {
			return Number(window.localStorage.getItem('last-action'));
		};

		const deleteIdleTime = (): void => {
			window.localStorage.removeItem('last-action');
		};
		window.addEventListener('mouseover', setIdleTime);

		window.addEventListener('click', setIdleTime);

		window.addEventListener('keypress', setIdleTime);
	},

	/**
	 * Note that `getUserProfile` returns an object value that differs in format from the
	 * `profile` argument accepted by {@link User#saveUserProfile}. This method's result is an
	 * object wrapper around the `profile` object; e.g.:
	 *
	 * ```json
	 * {
	 *     "profile": {
	 *         "first_name": "Joe",
	 *         // ...
	 *     }
	 * }
	 * ```
	 *
	 * This inconsistency was probably a design mistake but is left as-is for backward
	 * compatibility.
	 *
	 * @returns {Promise<AxiosResponse<Object>>} A wrapper object around the profile:
	 */
	getUserProfile: async () => {
		return await axios({
			method: 'GET',
			url: process.env.VUE_APP_API_URI + '/profile',
		});
	},
	getUser: async () => {
		return await axios({
			method: 'GET',
			url: process.env.VUE_APP_API_URI + '/profile?coreUser=true',
		});
	},

	/**
	 * Note that `saveUserProfile` accepts a `profile` argument object that differs in format from
	 * the value returned by {@link User#getUserProfile}. This method's argument is a profile
	 * object without a wrapper around it; e.g.:
	 *
	 * ```json
	 * {
	 *     "first_name": "Joe",
	 *     // ...
	 * }
	 * ```
	 *
	 * This inconsistency was probably a design mistake but is left as-is for backward
	 * compatibility.
	 */
	saveUserProfile: async profile => {
		return await axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI + '/profile',
			data: profile,
		});
	},

	checkRegistered: async (): Promise<boolean> => {
		let policies: PolicyUi[] = store.getters['Policy/policies'];
		if (policies.length === 0) {
			policies = await Policy.setPolicyData();
		}

		// If a user has policies, they are considered registered.
		if (policies.length > 0) {
			return true;
		}

		// If a user has access to risk resources, they are considered registered.
		const riskResourcesRequested = store.getters['Registration/riskResourcesRequested'];
		if (riskResourcesRequested) {
			return true;
		}

		// If a user is an org admin or an agent, they are considered registered.
		if (Auth.checkPermission( 'ACCOUNT_ADMIN' ) || Auth.checkPermission( 'IS_AGENT' ) ) {
			return true;
		}

		return false;
	},

	postRegistration: async data => {
		return await axios({
			method: 'POST',
			url: process.env.VUE_APP_API_URI2 + '/register',
			data,
		});
	},
	declineInvitation: async data => {
		// @ts-ignore
		return await axios({
			method: 'DELETE',
			skipAuth: true,
			url: process.env.VUE_APP_API_URI + '/user-decline-invitation',
			data,
		});
	},
	checkVersion: async () => {
		// Get the version of the app
		// @ts-ignore
		return await axios({
			method: 'GET',
			skipAuth: true,
			url: process.env.VUE_APP_API_URI2 + '/version',
		});
	},
};

export default User;
